import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@clarix/ui';
import { QM_COPY } from './copy';

interface SignOutRecordingDialogProps {
  busy: boolean;
  error: string | null;
  onUpload: () => void;
  onDeleteLocal: () => void;
  /** Saves the audio still on this device to a file before choosing. */
  onDownload: () => void;
}

/**
 * Sign-out is held until the recording is saved; when the last parts can't upload, the user
 * must choose (QUICK-MEETING-PLAN §2.3). Not dismissable: sign-out waits on the answer.
 * Delete asks again in place, since a second stacked dialog can't sit over this one.
 */
export function SignOutRecordingDialog({ busy, error, onUpload, onDeleteLocal, onDownload }: SignOutRecordingDialogProps) {
  const copy = QM_COPY.signOut;
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <AlertDialog open>
      <AlertDialogContent className="max-w-md" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmDelete ? QM_COPY.deleteLocalTitle : copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{confirmDelete ? QM_COPY.deleteLocalBody : copy.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {!confirmDelete && <p className="text-md text-muted-foreground">{copy.footnote}</p>}
        {error && (
          <p className="text-md text-destructive-fg" role="alert">
            {error}
          </p>
        )}
        <AlertDialogFooter className="gap-2">
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onDownload}>
            <Download aria-hidden />
            {QM_COPY.downloadFromDevice}
          </Button>
          {confirmDelete ? (
            <>
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setConfirmDelete(false)}>
                {copy.back}
              </Button>
              <Button type="button" variant="destructive" size="sm" disabled={busy} onClick={onDeleteLocal}>
                {busy && <Loader2 className="animate-spin" aria-hidden />}
                {QM_COPY.deleteLocalConfirm}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setConfirmDelete(true)}>
                {copy.deleteLocal}
              </Button>
              <Button type="button" size="sm" disabled={busy} onClick={onUpload}>
                {busy && <Loader2 className="animate-spin" aria-hidden />}
                {busy ? copy.uploading : copy.upload}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
